import easyCheckApi from '../api/config';

// Zoho Books integration — API Pull method (alongside the existing webhook).
// Endpoints live under api/zoho/*. Setup calls are Admin-only and need a plan with zohoIntegration
// (Business Plus / Premium). The default integration method is WEBHOOK, so companies that never
// open the Zoho setup page see no change.

const pick = (obj, pascal, camel, fallback = null) => {
  if (!obj) return fallback;
  const value = obj[pascal] ?? obj[camel];
  return value === undefined || value === null ? fallback : value;
};

// Backend sends PascalCase `Message`; the shared handleApiError only reads `message` and masks long
// messages, so surface the backend text verbatim (same approach as voucherService).
const backendMessage = (error, fallback) => {
  const body = error?.response?.data;
  const message = body?.Message || body?.message || body?.ErrorDetails;
  if (message) return message;
  const status = error?.response?.status;
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have access to the Zoho Books integration';
  return error?.message || fallback;
};

const request = async (call, fallback) => {
  try {
    const response = await call();
    const body = response.data || {};
    const success = body.Success ?? body.success ?? true;
    if (!success) throw new Error(body.Message || body.message || fallback);
    return body.Data ?? body.data ?? null;
  } catch (error) {
    if (error?.response) throw new Error(backendMessage(error, fallback));
    throw error instanceof Error ? error : new Error(fallback);
  }
};

const normalizeSettings = (s) => ({
  planAllowed: Boolean(pick(s, 'PlanAllowed', 'planAllowed', false)),
  isAdmin: Boolean(pick(s, 'IsAdmin', 'isAdmin', false)),
  importAvailable: Boolean(pick(s, 'ImportAvailable', 'importAvailable', false)),
  integrationMethod: pick(s, 'IntegrationMethod', 'integrationMethod', 'WEBHOOK'),
  connStatus: pick(s, 'ConnStatus', 'connStatus', 'NOT_CONNECTED'),
  dataCentre: pick(s, 'DataCentre', 'dataCentre', ''),
  clientId: pick(s, 'ClientId', 'clientId', ''),
  hasCredentials: Boolean(pick(s, 'HasCredentials', 'hasCredentials', false)),
  organizationId: pick(s, 'OrganizationId', 'organizationId', ''),
  organizationName: pick(s, 'OrganizationName', 'organizationName', ''),
  chqNoFieldName: pick(s, 'ChqNoFieldName', 'chqNoFieldName', ''),
  lastError: pick(s, 'LastError', 'lastError', ''),
  connectedAt: pick(s, 'ConnectedAt', 'connectedAt'),
  connectedBy: pick(s, 'ConnectedBy', 'connectedBy'),
  lastPullAt: pick(s, 'LastPullAt', 'lastPullAt'),
  lastPullBy: pick(s, 'LastPullBy', 'lastPullBy'),
  requiredScopes: pick(s, 'RequiredScopes', 'requiredScopes', ''),
  dataCentres: pick(s, 'DataCentres', 'dataCentres', ['com', 'eu', 'in', 'com.au', 'jp', 'sa', 'ca']),
});

const normalizeOrg = (o) => ({
  organizationId: pick(o, 'OrganizationId', 'organizationId', ''),
  name: pick(o, 'Name', 'name', ''),
  currencyCode: pick(o, 'CurrencyCode', 'currencyCode', ''),
});

const normalizePreviewRow = (r) => ({
  zohoPaymentId: pick(r, 'ZohoPaymentId', 'zohoPaymentId', ''),
  paymentNumber: pick(r, 'PaymentNumber', 'paymentNumber', ''),
  vendorName: pick(r, 'VendorName', 'vendorName', ''),
  amount: Number(pick(r, 'Amount', 'amount', 0)),
  currencyCode: pick(r, 'CurrencyCode', 'currencyCode', ''),
  paymentDate: pick(r, 'PaymentDate', 'paymentDate', ''),
  paymentMode: pick(r, 'PaymentMode', 'paymentMode', ''),
  referenceNumber: pick(r, 'ReferenceNumber', 'referenceNumber', ''),
  chequeNumber: pick(r, 'ChequeNumber', 'chequeNumber', ''),
  description: pick(r, 'Description', 'description', ''),
  zohoAccountName: pick(r, 'ZohoAccountName', 'zohoAccountName', ''),
  bankId: pick(r, 'BankId', 'bankId'),
  bankAccountName: pick(r, 'BankAccountName', 'bankAccountName', ''),
  bankCurrency: pick(r, 'BankCurrency', 'bankCurrency', ''),
  status: pick(r, 'Status', 'status', ''),
  statusMessage: pick(r, 'StatusMessage', 'statusMessage', ''),
});

const normalizeImportRow = (r) => ({
  zohoPaymentId: pick(r, 'ZohoPaymentId', 'zohoPaymentId', ''),
  success: Boolean(pick(r, 'Success', 'success', false)),
  status: pick(r, 'Status', 'status', 'FAILED'),
  message: pick(r, 'Message', 'message', ''),
  printedChequeId: pick(r, 'PrintedChequeId', 'printedChequeId'),
  chequeNumber: pick(r, 'ChequeNumber', 'chequeNumber', ''),
  vendorName: pick(r, 'VendorName', 'vendorName', ''),
  amount: pick(r, 'Amount', 'amount'),
});

// The backend imports at most 50 payments per request
const IMPORT_CHUNK_SIZE = 50;

// The webhook receiver lives on the same API host (axios baseURL), e.g. .../easycheck/api/zohobookswebhook
export const ZOHO_WEBHOOK_URL = `${(easyCheckApi.defaults.baseURL || '').replace(/\/+$/, '')}/zohobookswebhook`;

const normalizeWebhookKeys = (data) => ({
  keys: (pick(data, 'Keys', 'keys', []) || []).map((k) => ({
    id: pick(k, 'Id', 'id'),
    apiKey: pick(k, 'ApiKey', 'apiKey', ''),
    isActive: Boolean(pick(k, 'IsActive', 'isActive', false)),
    createdAt: pick(k, 'CreatedAt', 'createdAt'),
    updatedAt: pick(k, 'UpdatedAt', 'updatedAt'),
    notes: pick(k, 'Notes', 'notes', ''),
  })),
  canCreate: Boolean(pick(data, 'CanCreate', 'canCreate', false)),
  planAllowed: Boolean(pick(data, 'PlanAllowed', 'planAllowed', false)),
  headerName: pick(data, 'HeaderName', 'headerName', 'X-API-Key'),
});

export const zohoService = {
  // ----- Webhook keys (Admin). Old keys stay active until deactivated. Blank apiKey = auto-generate. -----

  getWebhookKeys: async () =>
    normalizeWebhookKeys(await request(() => easyCheckApi.get('/zoho/webhook-keys'), 'Failed to load webhook keys')),

  createWebhookKey: async (apiKey = '') =>
    normalizeWebhookKeys(await request(() => easyCheckApi.post('/zoho/webhook-keys', { apiKey }), 'Failed to create webhook key')),

  updateWebhookKey: async (id, apiKey = '') =>
    normalizeWebhookKeys(await request(() => easyCheckApi.put(`/zoho/webhook-keys/${id}`, { apiKey }), 'Failed to update webhook key')),

  setWebhookKeyActive: async (id, active) =>
    normalizeWebhookKeys(
      await request(
        () => easyCheckApi.post(`/zoho/webhook-keys/${id}/${active ? 'activate' : 'deactivate'}`),
        'Failed to update webhook key'
      )
    ),

  /** GET api/zoho/settings — any authenticated user. */
  getSettings: async () =>
    normalizeSettings(await request(() => easyCheckApi.get('/zoho/settings'), 'Failed to load Zoho settings')),

  /** POST api/zoho/connect — exchanges a Self Client grant code. Returns { settings, organizations }. */
  connect: async ({ dataCentre, clientId, clientSecret, grantCode }) => {
    const data = await request(
      () => easyCheckApi.post('/zoho/connect', { dataCentre, clientId, clientSecret, grantCode }, { timeout: 60000 }),
      'Failed to connect to Zoho Books'
    );
    return {
      settings: normalizeSettings(pick(data, 'Settings', 'settings', {})),
      organizations: (pick(data, 'Organizations', 'organizations', []) || []).map(normalizeOrg),
    };
  },

  getOrganizations: async () =>
    ((await request(() => easyCheckApi.get('/zoho/organizations'), 'Failed to load Zoho organizations')) || []).map(normalizeOrg),

  saveSettings: async ({ organizationId, chqNoFieldName }) =>
    normalizeSettings(await request(() => easyCheckApi.put('/zoho/settings', { organizationId, chqNoFieldName }), 'Failed to save Zoho settings')),

  saveMethod: async (integrationMethod) =>
    normalizeSettings(await request(() => easyCheckApi.put('/zoho/method', { integrationMethod }), 'Failed to save integration method')),

  disconnect: async () =>
    normalizeSettings(await request(() => easyCheckApi.post('/zoho/disconnect'), 'Failed to disconnect Zoho Books')),

  /** GET api/zoho/bank-accounts — Zoho bank accounts (with current mapping) + EasyCheck banks. */
  getBankAccounts: async () => {
    const data = await request(() => easyCheckApi.get('/zoho/bank-accounts', { timeout: 60000 }), 'Failed to load bank accounts');
    return {
      zohoAccounts: (pick(data, 'ZohoAccounts', 'zohoAccounts', []) || []).map((a) => ({
        accountId: pick(a, 'AccountId', 'accountId', ''),
        accountName: pick(a, 'AccountName', 'accountName', ''),
        accountType: pick(a, 'AccountType', 'accountType', ''),
        currencyCode: pick(a, 'CurrencyCode', 'currencyCode', ''),
        mappedBankId: pick(a, 'MappedBankId', 'mappedBankId'),
      })),
      banks: (pick(data, 'Banks', 'banks', []) || []).map((b) => ({
        id: pick(b, 'Id', 'id'),
        accountName: pick(b, 'AccountName', 'accountName', ''),
        accountNumber: pick(b, 'AccountNumber', 'accountNumber', ''),
        currency: pick(b, 'Currency', 'currency', ''),
      })),
    };
  },

  /** PUT api/zoho/bank-mapping — mappings: [{ zohoAccountId, zohoAccountName, bankId|null }] */
  saveBankMapping: async (mappings) =>
    request(() => easyCheckApi.put('/zoho/bank-mapping', { mappings }), 'Failed to save bank mapping'),

  /** POST api/zoho/preview — read-only; fromDate/toDate as yyyy-MM-dd strings. */
  preview: async (fromDate, toDate) => {
    const data = await request(
      () => easyCheckApi.post('/zoho/preview', { fromDate, toDate }, { timeout: 180000 }),
      'Failed to read payments from Zoho Books'
    );
    return {
      rows: (pick(data, 'Rows', 'rows', []) || []).map(normalizePreviewRow),
      totalFetched: Number(pick(data, 'TotalFetched', 'totalFetched', 0)),
      nonChequeCount: Number(pick(data, 'NonChequeCount', 'nonChequeCount', 0)),
      truncated: Boolean(pick(data, 'Truncated', 'truncated', false)),
    };
  },

  /**
   * POST api/zoho/import — imports the given Zoho payment ids as Pending cheques, in chunks of 50.
   * onProgress(done, total) is called after each chunk. Stops early if a chunk fails outright.
   */
  importPayments: async (paymentIds, onProgress) => {
    const results = [];
    for (let i = 0; i < paymentIds.length; i += IMPORT_CHUNK_SIZE) {
      const chunk = paymentIds.slice(i, i + IMPORT_CHUNK_SIZE);
      const data = await request(
        () => easyCheckApi.post('/zoho/import', { paymentIds: chunk }, { timeout: 180000 }),
        'Failed to import payments from Zoho Books'
      );
      results.push(...(pick(data, 'Results', 'results', []) || []).map(normalizeImportRow));
      if (onProgress) onProgress(Math.min(i + chunk.length, paymentIds.length), paymentIds.length);
    }
    return {
      results,
      importedCount: results.filter((r) => r.status === 'IMPORTED').length,
      skippedCount: results.filter((r) => r.status === 'SKIPPED').length,
      failedCount: results.filter((r) => r.status === 'FAILED').length,
    };
  },
};

export default zohoService;
